// frontend/src/pages/admin/EditProductPage.tsx
import { useState, useEffect, type FormEvent, type ChangeEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { ArrowLeft, Save, Loader2, AlertCircle, CheckCircle, ShoppingBag, Eye, Code } from 'lucide-react';
import RichTextEditor from '@/components/RichTextEditor';
import { getProductByIdOrSlug, updateProduct, uploadProductImage } from '@/api/productService';
import { type Product, type ProductUpdatePayload, type ProductStatus } from '@/types';

const slugify = (text: string): string => {
  return text.toString().toLowerCase().trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')
    .replace(/--+/g, '-');
};

// Utility function to unescape HTML entities
function unescapeHtml(html: string): string {
  if (typeof document === 'undefined') { // Basic SSR guard
    return html
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#039;/g, "'");
  }
  const txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value;
}

const EditProductPage = () => {
  const navigate = useNavigate();
  const { productIdOrSlug } = useParams<{ productIdOrSlug: string }>();

  const [initialProductData, setInitialProductData] = useState<Product | null>(null);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [details, setDetails] = useState('');
  const [status, setStatus] = useState<ProductStatus>('coming_soon');
  
  const [currentImageUrl, setCurrentImageUrl] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [showRawHtmlDetails, setShowRawHtmlDetails] = useState(false); 

  const backendBaseUrl = import.meta.env.VITE_API_BASE_URL?.replace('/api/v1', '') || 'http://localhost:8000';

  useEffect(() => {
    if (!productIdOrSlug) {
      setError("Product identifier is missing."); setIsLoading(false); return;
    }
    const fetchProduct = async () => {
      setIsLoading(true);
      try {
        const data = await getProductByIdOrSlug(productIdOrSlug);
        setInitialProductData(data); 
        setName(data.name);
        setSlug(data.slug);
        setDescription(data.description || '');
        setDetails(data.details ? unescapeHtml(data.details) : ''); // << --- USE unescapeHtml HERE
        setStatus(data.status);
        setCurrentImageUrl(data.image_url || null); 
        if (data.image_url) { 
          setImagePreview(data.image_url.startsWith('http') ? data.image_url : `${backendBaseUrl}/static/${data.image_url}`);
        } else {
          setImagePreview(null);
        }
        setError(null);
      } catch (err) {
        console.error("Failed to fetch product:", err);
        setError("Failed to load product data. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchProduct();
  }, [productIdOrSlug, backendBaseUrl]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setName(newName);
    if (initialProductData && (slug === slugify(initialProductData.name) || slug === '')) {
      setSlug(slugify(newName));
    }
  };
  
  const handleImageFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setError("Invalid file type. Please select an image.");
        setImageFile(null);
        setImagePreview(currentImageUrl ? (currentImageUrl.startsWith('http') ? currentImageUrl : `${backendBaseUrl}/static/${currentImageUrl}`) : null);
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file)); 
      setError(null);
      setIsUploadingImage(true);
      try {
        const response = await uploadProductImage(file);
        setCurrentImageUrl(response.image_url); 
      } catch (uploadError: any) {
        setError(uploadError.response?.data?.detail || "Product image upload failed.");
        setImagePreview(initialProductData?.image_url ? (initialProductData.image_url.startsWith('http') ? initialProductData.image_url : `${backendBaseUrl}/static/${initialProductData.image_url}`) : null);
        setCurrentImageUrl(initialProductData?.image_url || null); 
      } finally {
        setIsUploadingImage(false);
      }
    } else { 
      setImageFile(null);
      setImagePreview(currentImageUrl ? (currentImageUrl.startsWith('http') ? currentImageUrl : `${backendBaseUrl}/static/${currentImageUrl}`) : null);
    }
  };

  const handleDetailsChange = (content: string) => { 
    setDetails(content);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!initialProductData) { setError("Product data not loaded."); return; }
    setIsSubmitting(true); setError(null); setSuccessMessage(null);

    if (!name || !slug) {
      setError("Product Name and Slug are required."); setIsSubmitting(false); return;
    }

    const payload: ProductUpdatePayload = {
      name, slug,
      description: description || null,
      details: details || null, 
      status,
      image_url: currentImageUrl, 
    };

    try {
      const updatedProduct = await updateProduct(initialProductData.id, payload);
      setSuccessMessage(`Product "${updatedProduct.name}" updated successfully! Redirecting...`);
      setInitialProductData(updatedProduct);
      setTimeout(() => { navigate('/admin/products'); }, 2000);
    } catch (err: any) {
      if (err.response && err.response.data && err.response.data.detail) {
        setError(typeof err.response.data.detail === 'string' ? err.response.data.detail : JSON.stringify(err.response.data.detail));
      } else {
        setError("Failed to update product. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Loading and error states for initial fetch
  if (isLoading) { 
    return (
        <div className="container mx-auto px-4 py-20 flex justify-center">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </div>
    );
  }
  if (error && !initialProductData) { 
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
        <p className="text-xl text-destructive">{error}</p>
        <Button onClick={() => navigate("/admin/products")} className="mt-4">Go Back</Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 md:py-12">
      <div className="mb-6">
        <Button variant="outline" onClick={() => navigate("/admin/products")} className="text-primary hover:text-primary/80">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Manage Products
        </Button>
      </div>

      <Card className="max-w-3xl mx-auto bg-card border-border/30 shadow-lg">
        <CardHeader>
         <div className="flex items-center gap-3">
            <ShoppingBag className="h-8 w-8 text-primary" />
            <CardTitle className="text-2xl md:text-3xl text-foreground">Edit Product</CardTitle>
          </div>
          <CardDescription className="text-muted-foreground pt-1">Modify the details for "{initialProductData?.name || 'product'}".</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-6">
            {error && (
              <div className="p-3 my-4 bg-destructive/10 text-destructive border border-destructive/30 rounded-md flex items-center">
                <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}
            {successMessage && (
              <div className="p-3 my-4 bg-green-500/10 text-green-700 dark:text-green-400 border border-green-500/30 rounded-md flex items-center">
                <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                <span className="text-sm">{successMessage}</span>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="name" className="text-foreground">Product Name <span className="text-destructive">*</span></Label>
              <Input id="name" value={name} onChange={handleNameChange} placeholder="Enter product name" required disabled={isSubmitting || isUploadingImage} className="!bg-input border-border focus:border-primary focus:ring-primary" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="slug" className="text-foreground">Slug <span className="text-destructive">*</span></Label>
              <Input id="slug" value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="product-slug" required disabled={isSubmitting || isUploadingImage} className="!bg-input border-border focus:border-primary focus:ring-primary" />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="productImageEdit" className="text-foreground">Product Image (Optional)</Label>
              <div className="flex items-center gap-4">
                <Input
                  id="productImageEdit"
                  type="file"
                  accept="image/*"
                  onChange={handleImageFileChange}
                  className="!bg-input border-border focus:border-primary focus:ring-primary file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20 cursor-pointer"
                  disabled={isUploadingImage || isSubmitting}
                />
                {isUploadingImage && <Loader2 className="h-5 w-5 animate-spin text-primary" />}
              </div>
              {imagePreview && (
                <div className="mt-2">
                  <img src={imagePreview} alt="Product Preview" className="h-32 w-auto object-contain border border-border rounded-md p-1 bg-muted/10" />
                  {currentImageUrl && !imageFile && <p className="text-xs text-muted-foreground mt-1">Current image: {currentImageUrl.split('/').pop()}</p> }
                  {imageFile && currentImageUrl && <p className="text-xs text-green-600 mt-1">New image selected and uploaded. Relative path: {currentImageUrl}</p>}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="text-foreground">Short Description (Optional)</Label>
              <Textarea id="description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Brief summary of the product" rows={3} disabled={isSubmitting || isUploadingImage} className="!bg-input border-border focus:border-primary focus:ring-primary min-h-[80px]" />
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center mb-1">
                <Label htmlFor="details" className="text-foreground">Full Details (Optional)</Label>
                <Button type="button" variant="outline" size="sm" onClick={() => setShowRawHtmlDetails(!showRawHtmlDetails)} className="text-xs">
                  {showRawHtmlDetails ? <><Eye className="mr-1 h-3 w-3" /> View Rendered (Rich Text)</> : <><Code className="mr-1 h-3 w-3" /> View Raw HTML</>}
                </Button>
              </div>
              {showRawHtmlDetails ? (
                <Textarea 
                    value={details} 
                    onChange={(e) => handleDetailsChange(e.target.value)} 
                    className="w-full min-h-[250px] p-2 border-input rounded-md font-mono text-sm bg-muted/30 dark:bg-input/80" 
                    placeholder="Enter raw HTML for product details..."
                    disabled={isSubmitting || isUploadingImage}
                />
              ) : (
                <div className="border border-input rounded-md p-1 bg-background shadow-sm">
                  <RichTextEditor key={initialProductData?.id || 'edit-product-rte'} value={details} onChange={handleDetailsChange} />
                </div>
              )}
              <p className="text-xs text-muted-foreground">Use the rich text editor or raw HTML for detailed product information.</p>
            </div>
            
             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                    <Label htmlFor="status" className="text-foreground">Status</Label>
                    <Select value={status} onValueChange={(value: ProductStatus) => setStatus(value)} disabled={isSubmitting || isUploadingImage}>
                        <SelectTrigger className="!bg-input border-border focus:border-primary focus:ring-primary">
                        <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent className="bg-popover border-border">
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                        <SelectItem value="coming_soon">Coming Soon</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

          </CardContent>
          <CardFooter className="border-t border-border/30 pt-6">
            <Button type="submit" className="w-full md:w-auto !bg-gradient-to-r !from-primary !to-accent hover:!from-primary/90 hover:!to-accent/90 text-primary-foreground" disabled={isSubmitting || isLoading || isUploadingImage}>
              {isSubmitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Updating Product...</> : <><Save className="mr-2 h-4 w-4" /> Save Changes</>}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default EditProductPage;